// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: Settings → Assistant → Suggestions (design/assistant-engagement s17). How much the corner
// assistant suggests: pop-ups on or off, whether they pop up or only wait in the From Konvo list,
// mornings kept clear, how many a day (meeting prep is not counted), quiet hours,
// weekends, push when Konvo is closed, each kind of pop-up (a paused kind says until when; switching
// it on resumes it), the starting points in Mail, Tasks and Calendar, and what happened to the
// recent suggestions. Every change saves at once.

import {SettingsRow} from '@app/features/app/components/dialogs/shared/SettingsRow';
import {SettingsSection} from '@app/features/app/components/dialogs/shared/SettingsSection';
import {KIND_ICON} from '@app/features/konvo_corner/components/CornerCard';
import {CORNER_SETTINGS_SECTION_ID} from '@app/features/konvo_corner/components/CornerSettingsLink';
import styles from '@app/features/konvo_corner/components/CornerSuggestionsSettings.module.css';
import Corner from '@app/features/konvo_corner/state/Corner';
import CornerSettings, {type CornerSettingsPatch} from '@app/features/konvo_corner/state/CornerSettings';
import EngagementAccess from '@app/features/konvo_engagement/state/EngagementAccess';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import {Switch} from '@app/features/ui/components/form/FormSwitch';
import {ClickUpLogo} from '@app/features/ui/components/icons/ClickUpLogo';
import {
	BellIcon,
	CalendarBlankIcon,
	CalendarDotsIcon,
	ChatCircleDotsIcon,
	CheckSquareIcon,
	ClockIcon,
	ListChecksIcon,
	MoonIcon,
	SparkleIcon,
	TagIcon,
	UsersIcon,
} from '@app/features/ui/icons/Icons';
import {Segmented} from '@app/features/ui/konvo_controls/KonvoControls';
import {
	CORNER_PER_DAY_CHOICES,
	type CornerKind,
	type CornerRecent,
	CornerSettingsSchema,
	type CornerStatus,
} from '@konvo/schema/src/domains/konvo/AssistantEngagement';
import type {I18n, MessageDescriptor} from '@lingui/core';
import {msg} from '@lingui/core/macro';
import {Trans, useLingui} from '@lingui/react/macro';
import {IconClock, IconHourglassHigh} from '@tabler/icons-react';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useEffect, useId} from 'react';

const DEFAULTS = CornerSettingsSchema.parse({});
const FAILED = msg({message: 'That did not save. Try again.', comment: 'Toast when a suggestion setting fails.'});

const PER_DAY: Record<(typeof CORNER_PER_DAY_CHOICES)[number], {name: MessageDescriptor; line: MessageDescriptor}> = {
	1: {
		name: msg({message: 'Quiet', comment: 'Suggestions a day: 1.'}),
		line: msg({message: '1 pop-up a day', comment: 'Under "Quiet" in Suggestions a day.'}),
	},
	2: {
		name: msg({message: 'Balanced', comment: 'Suggestions a day: 2 (default).'}),
		line: msg({message: '2 pop-ups a day', comment: 'Under "Balanced" in Suggestions a day.'}),
	},
	4: {
		name: msg({message: 'Helpful', comment: 'Suggestions a day: 4.'}),
		line: msg({message: '4 pop-ups a day', comment: 'Under "Helpful" in Suggestions a day.'}),
	},
};

const POPUPS_TITLE = msg({
	message: 'Pop-ups',
	comment:
		'Settings → Assistant → Suggestions: heading of the choice whether suggestions pop up in the corner or only wait in the From Konvo list.',
});
const POPUPS_ON = msg({
	message: 'On',
	comment: 'Settings → Assistant → Suggestions, Pop-ups: suggestions pop up in the corner.',
});
const POPUPS_LIST_ONLY = msg({
	message: 'Only in the list',
	comment:
		'Settings → Assistant → Suggestions, Pop-ups: nothing pops up; everything waits in the From Konvo list behind the Konvo icon.',
});
const POPUPS_NOTE = msg({
	message: 'With Only in the list nothing pops up, meeting prep included. Click the Konvo icon to see what is ready.',
	comment: 'Settings → Assistant → Suggestions: note under the Pop-ups choice.',
});
const MORNINGS = msg({
	message: 'Keep mornings clear',
	comment: 'Settings → Assistant → Suggestions: switch that stops pop-ups before a set time of day.',
});
const MORNINGS_LINE = msg({
	message: 'Nothing pops up before {time}. What is ready earlier waits in the list.',
	comment: 'Settings → Assistant → Suggestions: under "Keep mornings clear". time is a clock time such as 13:00.',
});
/** "Keep mornings clear" switched on here: the same time "Not in the morning" sets. */
const MORNINGS_FROM = '13:00';

const LEAD_TIMES = [5, 10, 15, 30] as const;
const LEAD_LABEL = msg({
	message: '{minutes} min before',
	comment: 'Meeting prep lead time option. minutes is 5, 10, 15 or 30.',
});

const STATUS_LABEL: Record<CornerStatus | 'paused', MessageDescriptor> = {
	planned: msg({message: 'Planned', comment: 'Recent suggestion status.'}),
	held: msg({message: 'Waiting', comment: 'Recent suggestion status: held until you were free.'}),
	shown: msg({message: 'Shown', comment: 'Recent suggestion status.'}),
	used: msg({message: 'Used', comment: 'Recent suggestion status.'}),
	later: msg({message: 'Later', comment: 'Recent suggestion status.'}),
	dismissed: msg({message: 'Dismissed', comment: 'Recent suggestion status.'}),
	timed_out: msg({message: 'Ignored', comment: 'Recent suggestion status: closed by itself.'}),
	skipped: msg({message: 'Skipped', comment: 'Recent suggestion status.'}),
	expired: msg({message: 'Missed', comment: 'Recent suggestion status: never shown in time.'}),
	paused: msg({message: 'Paused', comment: 'Recent suggestion status: this kind paused itself.'}),
};
const STATUS_TONE: Record<CornerStatus | 'paused', 'green' | 'amber' | 'neutral' | 'quiet'> = {
	planned: 'quiet',
	held: 'quiet',
	shown: 'neutral',
	used: 'green',
	later: 'neutral',
	dismissed: 'neutral',
	timed_out: 'quiet',
	skipped: 'quiet',
	expired: 'quiet',
	paused: 'amber',
};

const TONE_CLASS: Record<'green' | 'amber' | 'neutral' | 'quiet', string> = {
	green: styles.pillGreen,
	amber: styles.pillAmber,
	neutral: styles.pillNeutral,
	quiet: styles.pillQuiet,
};

const TODAY_AT = msg({message: 'Today {time}', comment: 'When a recent suggestion appeared. time is like 10:45.'});
const PAUSED_UNTIL = msg({
	message: 'Paused until {date}',
	comment: 'Pill on a suggestion kind that paused itself after being ignored 3 times. date is like "15 Oct".',
});

interface KindRow {
	kind: Exclude<CornerKind, 'connect_invite'>;
	title: MessageDescriptor;
	description: MessageDescriptor;
	icon: React.ReactNode;
}

const KIND_ROWS: ReadonlyArray<KindRow> = [
	{
		kind: 'waiting_on_you',
		title: msg({message: 'Waiting on you', comment: 'Settings: kind of corner suggestion.'}),
		description: msg({
			message: 'Someone asked you something and is still waiting for the answer',
			comment: 'Settings: corner suggestion kind that replaced client follow-ups.',
		}),
		icon: <IconClock size={20} stroke={1.8} />,
	},
	{
		kind: 'waiting_on_them',
		title: msg({message: 'No answer yet', comment: 'Settings: kind of corner suggestion.'}),
		description: msg({
			message: 'You asked someone something and the answer is late',
			comment: 'Settings: corner suggestion kind.',
		}),
		icon: <IconHourglassHigh size={20} stroke={1.8} />,
	},
	{
		kind: 'outstanding_work',
		title: msg({message: 'Outstanding work', comment: 'Settings: kind of corner suggestion.'}),
		description: msg({message: 'Monday morning, if Tasks isn’t opened', comment: 'Settings.'}),
		icon: <ListChecksIcon size={20} />,
	},
	{
		kind: 'review_time',
		title: msg({message: 'Review my time', comment: 'Settings: kind of corner suggestion.'}),
		description: msg({message: 'Friday afternoon and month-end', comment: 'Settings.'}),
		icon: <ClockIcon size={20} />,
	},
	{
		kind: 'promise_due',
		title: msg({message: 'Promises due', comment: 'Settings: kind of corner suggestion.'}),
		description: msg({message: 'Things you said you’d do in mail or WhatsApp', comment: 'Settings.'}),
		icon: <CheckSquareIcon size={20} />,
	},
	{
		kind: 'agent_task',
		title: msg({message: 'Agent chats without a task', comment: 'Settings: kind of corner suggestion.'}),
		description: msg({
			message: 'Once per chat, when Konvo is not sure which ClickUp task it is for',
			comment: 'Settings: when the corner assistant asks which ClickUp task an agent chat is for.',
		}),
		icon: <ClickUpLogo size={20} />,
	},
];

function halfHours(current: string): Array<string> {
	const times: Array<string> = [];
	for (let minutes = 0; minutes < 24 * 60; minutes += 30) {
		times.push(`${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`);
	}
	if (current && !times.includes(current)) times.push(current);
	return times.sort();
}

function shortDate(date: Date, locale: string): string {
	return date.toLocaleDateString(locale, {day: 'numeric', month: 'short'});
}

export function recentWhen(iso: string, i18n: I18n, now = new Date()): string {
	const at = new Date(iso);
	if (Number.isNaN(at.getTime())) return '';
	const time = at.toLocaleTimeString(i18n.locale, {hour: '2-digit', minute: '2-digit', hour12: false});
	if (at.toDateString() === now.toDateString()) return i18n._(TODAY_AT, {time});
	return at.toLocaleDateString(i18n.locale, {weekday: 'short', day: 'numeric', month: 'short'});
}

function TimeSelect({
	value,
	label,
	disabled,
	onChange,
	flx,
}: {
	value: string;
	label: string;
	disabled: boolean;
	onChange: (value: string) => void;
	flx: string;
}) {
	return (
		<select
			className={styles.select}
			value={value}
			aria-label={label}
			disabled={disabled}
			onChange={(event) => onChange(event.target.value)}
			data-flx={flx}
		>
			{halfHours(value).map((time) => (
				<option key={time} value={time}>
					{time}
				</option>
			))}
		</select>
	);
}

const RecentList = observer(() => {
	const {i18n} = useLingui();
	const items = CornerSettings.recent;
	if (items == null) return null;
	if (items.length === 0)
		return (
			<p className={styles.note} data-flx="konvo-corner.settings.recent.empty">
				<Trans>Nothing suggested yet.</Trans>
			</p>
		);
	// The newest of a kind that is paused right now shows why it stopped.
	const pausedShown = new Set<CornerKind>();
	const statusOf = (item: CornerRecent['items'][number]): CornerStatus | 'paused' => {
		if (!pausedShown.has(item.kind) && CornerSettings.pausedUntil(item.kind) && item.status === 'timed_out') {
			pausedShown.add(item.kind);
			return 'paused';
		}
		return item.status;
	};
	return (
		<ul className={styles.recent} data-flx="konvo-corner.settings.recent">
			{items.map((item) => {
				const status = statusOf(item);
				return (
					<li key={item.id} className={styles.recentRow} data-status={status}>
						<span className={styles.recentIcon}>
							{item.kind === 'meeting_prep' ? <CalendarDotsIcon size={18} /> : KIND_ICON[item.kind](18)}
						</span>
						<span className={styles.recentText}>
							<span className={styles.recentTitle}>{item.title}</span>
							<span className={styles.recentWhen}>{recentWhen(item.at, i18n)}</span>
						</span>
						<span className={clsx(styles.pill, TONE_CLASS[STATUS_TONE[status]])}>{i18n._(STATUS_LABEL[status])}</span>
					</li>
				);
			})}
		</ul>
	);
});

/** Settings → Assistant → Suggestions, only for people the new assistant experience is on for. */
export const CornerSuggestionsSettings = observer(() => (EngagementAccess.enabled ? <SuggestionsSection /> : null));

const SuggestionsSection = observer(() => {
	const {i18n} = useLingui();
	const id = useId();
	useEffect(() => {
		void CornerSettings.load(true);
		void CornerSettings.loadRecent();
		void CornerSettings.loadNudges();
	}, []);
	const loaded = CornerSettings.settings != null;
	const settings = CornerSettings.settings ?? DEFAULTS;
	const off = !loaded || !settings.enabled;
	const save = (patch: CornerSettingsPatch) => {
		void CornerSettings.save(patch).catch(() => ToastCommands.error(i18n._(FAILED)));
	};
	const pausedPill = (kind: CornerKind) => {
		const until = CornerSettings.pausedUntil(kind);
		if (!until) return null;
		return (
			<span className={clsx(styles.pill, styles.pillAmber)} data-flx={`konvo-corner.settings.paused.${kind}`}>
				{i18n._(PAUSED_UNTIL, {date: shortDate(until, i18n.locale)})}
			</span>
		);
	};
	const setKind = (kind: CornerKind, value: boolean) => {
		const patch: CornerSettingsPatch = {kinds: {[kind]: value}};
		if (value && settings.paused_until[kind]) {
			const {[kind]: _resumed, ...rest} = settings.paused_until;
			patch.paused_until = rest;
		}
		save(patch);
	};
	const kindOn = (kind: CornerKind) => settings.kinds[kind] && !CornerSettings.pausedUntil(kind);

	return (
		<SettingsSection
			id={CORNER_SETTINGS_SECTION_ID}
			title={<Trans>Suggestions</Trans>}
			description={
				<Trans>
					A few times a day the corner assistant offers one thing with a clear reason. Choose how often and what about.
				</Trans>
			}
			data-flx="konvo-corner.settings"
		>
			<div className={styles.group} data-flx="konvo-corner.settings.popups">
				<h3 className={styles.groupTitle}>
					<Trans>Corner assistant pop-ups</Trans>
				</h3>
				<Switch
					label={i18n._(msg`Show suggestions`)}
					description={i18n._(msg`A short note from Konvo when there’s a clear reason`)}
					value={settings.enabled}
					disabled={!loaded}
					onChange={(enabled) => save({enabled})}
					icon={<SparkleIcon size={20} />}
					data-flx="konvo-corner.settings.enabled"
				/>
				<div className={styles.block}>
					<div className={styles.blockTitle} id={`${id}-popups`}>
						{i18n._(POPUPS_TITLE)}
					</div>
					<p className={styles.note}>{i18n._(POPUPS_NOTE)}</p>
					<Segmented<'on' | 'list_only'>
						value={settings.popups}
						options={[
							{
								value: 'on',
								disabled: off,
								label: i18n._(POPUPS_ON),
								children: i18n._(POPUPS_ON),
								'data-flx': 'konvo-corner.settings.popups-mode.on',
							},
							{
								value: 'list_only',
								disabled: off,
								label: i18n._(POPUPS_LIST_ONLY),
								children: i18n._(POPUPS_LIST_ONLY),
								'data-flx': 'konvo-corner.settings.popups-mode.list_only',
							},
						]}
						onChange={(popups) => save({popups})}
						aria-labelledby={`${id}-popups`}
						grow
						data-flx="konvo-corner.settings.popups-mode"
					/>
				</div>
				<Switch
					label={i18n._(MORNINGS)}
					description={i18n._(MORNINGS_LINE, {time: settings.popups_from || MORNINGS_FROM})}
					value={Boolean(settings.popups_from)}
					disabled={off || settings.popups === 'list_only'}
					onChange={(on) => save({popups_from: on ? MORNINGS_FROM : ''})}
					icon={<ClockIcon size={20} />}
					data-flx="konvo-corner.settings.mornings"
				/>
				<div className={styles.block}>
					<div className={styles.blockTitle} id={`${id}-per-day`}>
						<Trans>Suggestions a day</Trans>
					</div>
					<p className={styles.note}>
						<Trans>Meeting prep isn’t counted: every meeting gets one</Trans>
					</p>
					<Segmented<'1' | '2' | '4'>
						value={String(settings.per_day) as '1' | '2' | '4'}
						options={CORNER_PER_DAY_CHOICES.map((choice) => ({
							value: String(choice) as '1' | '2' | '4',
							disabled: off,
							label: i18n._(PER_DAY[choice].name),
							children: (
								<span className={styles.perDay}>
									<span>{i18n._(PER_DAY[choice].name)}</span>
									<small>{i18n._(PER_DAY[choice].line)}</small>
								</span>
							),
							'data-flx': `konvo-corner.settings.per-day.${choice}`,
						}))}
						onChange={(value) => save({per_day: Number(value) as 1 | 2 | 4})}
						aria-labelledby={`${id}-per-day`}
						grow
						className={styles.perDaySegmented}
						data-flx="konvo-corner.settings.per-day"
					/>
				</div>
				<SettingsRow
					icon={<MoonIcon size={20} />}
					title={<Trans>Quiet hours</Trans>}
					description={<Trans>No suggestions or pushes</Trans>}
					control={() => (
						<span className={styles.hours}>
							<TimeSelect
								value={settings.quiet_from}
								label={i18n._(msg`Quiet from`)}
								disabled={off}
								onChange={(quiet_from) => save({quiet_from})}
								flx="konvo-corner.settings.quiet-from"
							/>
							<span className={styles.to}>
								<Trans>to</Trans>
							</span>
							<TimeSelect
								value={settings.quiet_until}
								label={i18n._(msg`Quiet until`)}
								disabled={off}
								onChange={(quiet_until) => save({quiet_until})}
								flx="konvo-corner.settings.quiet-until"
							/>
						</span>
					)}
					data-flx="konvo-corner.settings.quiet"
				/>
				<Switch
					label={i18n._(msg`Weekends off`)}
					value={settings.weekends_off}
					disabled={off}
					onChange={(weekends_off) => save({weekends_off})}
					icon={<CalendarBlankIcon size={20} />}
					data-flx="konvo-corner.settings.weekends"
				/>
				<Switch
					label={i18n._(msg`Push when Konvo is closed`)}
					description={i18n._(msg`Meeting prep and promises due only`)}
					value={settings.push_when_closed}
					disabled={off}
					onChange={(push_when_closed) => save({push_when_closed})}
					icon={<BellIcon size={20} />}
					data-flx="konvo-corner.settings.push"
				/>
			</div>

			<div className={styles.group} data-flx="konvo-corner.settings.kinds">
				<h3 className={styles.groupTitle}>
					<Trans>What to suggest</Trans>
				</h3>
				<Switch
					label={i18n._(msg`Meeting prep`)}
					description={i18n._(msg`Before every meeting with outside guests or 3+ people`)}
					value={kindOn('meeting_prep')}
					disabled={off}
					onChange={(value) => setKind('meeting_prep', value)}
					icon={<CalendarDotsIcon size={20} />}
					accessory={
						<span className={styles.accessory}>
							{pausedPill('meeting_prep')}
							<select
								className={styles.select}
								value={settings.meeting_prep_lead_min}
								aria-label={i18n._(msg`How long before the meeting`)}
								disabled={off || !settings.kinds.meeting_prep}
								onChange={(event) => save({meeting_prep_lead_min: Number(event.target.value) as 5 | 10 | 15 | 30})}
								data-flx="konvo-corner.settings.lead"
							>
								{LEAD_TIMES.map((minutes) => (
									<option key={minutes} value={minutes}>
										{i18n._(LEAD_LABEL, {minutes})}
									</option>
								))}
							</select>
						</span>
					}
					data-flx="konvo-corner.settings.kind.meeting_prep"
				/>
				<Switch
					label={i18n._(msg`Internal meetings too`)}
					description={i18n._(msg`Stand-ups and recurring team meetings`)}
					value={settings.internal_meetings}
					disabled={off || !settings.kinds.meeting_prep}
					onChange={(internal_meetings) => save({internal_meetings})}
					icon={<UsersIcon size={20} />}
					data-flx="konvo-corner.settings.internal"
				/>
				{KIND_ROWS.map((row) => (
					<Switch
						key={row.kind}
						label={i18n._(row.title)}
						description={i18n._(row.description)}
						value={kindOn(row.kind)}
						disabled={off}
						onChange={(value) => setKind(row.kind, value)}
						icon={row.icon}
						accessory={pausedPill(row.kind)}
						data-flx={`konvo-corner.settings.kind.${row.kind}`}
					/>
				))}
				{Corner.stage === 'not_connected' && (
					<Switch
						label={i18n._(msg`Invitations to connect your assistant`)}
						description={i18n._(msg`Built from your own mail, calendar and tasks. Three at most.`)}
						value={kindOn('connect_invite')}
						disabled={off}
						onChange={(value) => setKind('connect_invite', value)}
						icon={<SparkleIcon size={20} />}
						data-flx="konvo-corner.settings.kind.connect_invite"
					/>
				)}
			</div>

			<div className={styles.group} data-flx="konvo-corner.settings.messages">
				<h3 className={styles.groupTitle}>
					<Trans>Messages from Konvo</Trans>
				</h3>
				<Switch
					label={i18n._(msg`Tips and nudges in your Konvo DM`)}
					description={i18n._(msg`Now and then, a few things worth trying with your assistant, from your own week`)}
					value={CornerSettings.nudgesEnabled ?? true}
					disabled={CornerSettings.nudgesEnabled == null}
					onChange={(enabled) =>
						void CornerSettings.saveNudges(enabled).catch(() => ToastCommands.error(i18n._(FAILED)))
					}
					icon={<ChatCircleDotsIcon size={20} />}
					data-flx="konvo-corner.settings.nudges"
				/>
			</div>

			<div className={styles.group} data-flx="konvo-corner.settings.starters">
				<h3 className={styles.groupTitle}>
					<Trans>Starting points</Trans>
				</h3>
				<Switch
					label={i18n._(msg`Show Konvo row in Mail, Tasks and Calendar`)}
					description={i18n._(msg`Suggested requests next to what you’re reading`)}
					value={settings.starters_enabled}
					disabled={!loaded}
					onChange={(starters_enabled) => save({starters_enabled})}
					icon={<SparkleIcon size={20} />}
					data-flx="konvo-corner.settings.starters-enabled"
				/>
				<SettingsRow
					icon={<TagIcon size={20} />}
					title={<Trans>Labels</Trans>}
					description={<Trans>Shrink to icons once you’ve used them</Trans>}
					disabled={!loaded || !settings.starters_enabled}
					control={() => (
						<select
							className={styles.select}
							value={settings.starter_labels}
							aria-label={i18n._(msg`Labels`)}
							disabled={!loaded || !settings.starters_enabled}
							onChange={(event) => save({starter_labels: event.target.value as 'always' | 'icons_after_use'})}
							data-flx="konvo-corner.settings.labels"
						>
							<option value="always">{i18n._(msg`Always`)}</option>
							<option value="icons_after_use">{i18n._(msg`Icons after use`)}</option>
						</select>
					)}
					data-flx="konvo-corner.settings.labels-row"
				/>
			</div>

			<div className={styles.group} data-flx="konvo-corner.settings.recent-group">
				<h3 className={styles.groupTitle}>
					<Trans>Recent suggestions</Trans>
				</h3>
				<RecentList />
			</div>
		</SettingsSection>
	);
});
